/**
 * Output-size estimate for the Output line (PLAN.md C1, fix-all 2026-10-06; findings firsttime-2, phone-8,
 * power-6, formats-9, geometry-8). The old per-format constants assumed every photo was a 0.2 B/px JPEG, so
 * a 163 KB phone photo shrunk to 1600 px read "+134%" while the real file was −88%.
 *
 * Calibrated on the SOURCE's own bytes per pixel, which says how much detail the photo has:
 *  1. turn the source into a "JPEG-at-q85 equivalent" density `e` (B/px at the source's size);
 *  2. convert `e` to the output format at the chosen quality (lossless outputs grow steeply with detail);
 *  3. scale by the pixel ratio with a sublinear exponent (fewer pixels = more detail per pixel).
 * Constants fitted 2026-10-06 on the QA fixtures (quad_land, IMG_2041, family_photo, photo1600x1200,
 * grid1200x800, IMG_20261006_100001) re-encoded at r = 1, ½, ¼, 1/10 (fixall/L1-model/calib.js) and on
 * the Chrome outputs QA recorded; within ±30 % on photos, and the sign (grow / shrink) is right.
 * Final sweep 2026-10-06 (formats-9, NEW-4): opaque PNGs are the editor's own RGB writer, PSD is layer +
 * composite RLE, HEIC/WebP sources fitted on the server's and Chrome's outputs, no cap on dense JPEGs,
 * shrink exponent by detail; sizeDelta says "same" inside ±10 % (the estimate cannot sign it there).
 * Uncompressed outputs (BMP, PPM) are exact; TIFF is compressed now (tiffBytes). Usage QA 2026-10-07 refit:
 * dense sources shrink faster than linear, HEIC sources by density, browser AVIF, server routes. Pure: no DOM.
 */
import type { Size } from './edit-stack';

/** `jpeg`: the source JPEG's IJG quality and chroma subsampling (read from its header, jpegInfo); `file`:
 *  the source itself — primeEstimateSource(file) reads its header once, later estimates look it up. */
export interface EstimateSource { bytes: number; width: number; height: number; format: string; jpeg?: JpegInfo | null; file?: Blob;
    /** Bits per sample of the source (TIFF: 1, 8, 16; codecs' decode result). Unset = 8. */
    bitsPerSample?: number;
    /** HEIC/AVIF: written by a phone's encoder (heifInfo); unset = read from `file` via primeEstimateSource. */
    heif?: HeifInfo | null;
    /** TIFF: its compression and, uncompressed, a content probe (tiffInfo / probeRawDensity); unset = read from `file`. */
    tiff?: TiffInfo | null;
    /** PNG: a palette (colour type 3) image (pngIndexed); unset = read from `file`. */
    indexed?: boolean | null; }
/** `compression`: TIFF tag 259 (1 none, 5 LZW, 7 / 6 JPEG, 8 / 32946 Deflate, 32773 PackBits). `rawDensity`: for an
 *  uncompressed source, the B/px our Deflate + Predictor-2 writer is expected to reach on its content (null: not probed). */
export interface TiffInfo { compression: number; samples: number; bits: number; firstStrip: number; rawDensity?: number | null;
    /** Byte order II (the high byte of a 16-bit sample is its second); PlanarConfiguration 2 (one plane per channel). */
    le?: boolean; planar?: boolean; }
/** `camera`: the HEIF came out of a phone (Apple's Mi* brands, or no `miaf` brand as Samsung writes), not
 *  libheif/x265 (`mif1 heic miaf`). Phone encoders are ~2x as dense as Chrome's JPEG at equal quality. */
export interface HeifInfo { camera: boolean; }
/** `fineRotation`: a non-90° turn (degrees) resamples every pixel and adds anti-aliased edges — PNG pays for
 *  them; `corners` = the frame grew to hold the turned photo (filled or transparent), false for auto-crop. */
export interface FineRotation { angle: number; corners: boolean; }
/** `route` (usage QA 2026-10-07, J8-5 / J13-3 / J3-5): where the file is written. Our servers keep a TIFF's bit
 *  depth and LZW, and their HEIC/AVIF encoder is far less dense than the browser's; unset = the browser. */
export interface EstimateInput { size: Size; format: string; quality: number; hasAlpha: boolean; source?: EstimateSource; fineRotation?: FineRotation | null; route?: 'browser' | 'server';
    /** review9 IMAGEEDITOR-05: the share (0..1] of the source's pixels the output shows (a crop: its region), so the
     *  density is the region's, not the whole photo's; unset = 1. edit-stack `estimateHints`. */
    region?: number;
    /** The edit adds transparency (a Circle crop): the output is written with alpha whatever the source had. */
    shapeAlpha?: boolean;
    /** Any op resamples pixels (resize, fine turn, platform crop); unset = guessed from the pixel counts. An indexed
     *  PNG keeps its palette only without one (review9 save-as-3e). edit-stack `estimateHints`. */
    resampled?: boolean; }
export interface JpegInfo { quality: number; sub444: boolean; }

// ---------------------------------------------------------------- the source JPEG's header
const STD_LUMA_SUM = 3688;   // sum of the IJG / Annex K luminance table
/** IJG quality (1..100) and chroma subsampling from a JPEG head (SOI .. SOS); null when it is not one. */
export function jpegInfo(b: Uint8Array): JpegInfo | null {
    if (!b || b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return null;
    let i = 2, luma: number | null = null, sub444: boolean | null = null;
    while (i + 4 <= b.length) {
        if (b[i] !== 0xff) return null;
        const m = b[i + 1];
        if (m === 0xff) { i++; continue; }
        if (m === 0xda || m === 0xd9) break;                                  // SOS / EOI: header done
        const len = (b[i + 2] << 8) | b[i + 3];
        if (len < 2 || i + 2 + len > b.length) return null;
        if (m === 0xdb) {                                                     // DQT: one or more tables
            let j = i + 4;
            while (j < i + 2 + len) {
                const pq = b[j] >> 4, tq = b[j] & 15, n = pq ? 128 : 64; let sum = 0;
                for (let k = 0; k < 64; k++) sum += pq ? (b[j + 1 + 2 * k] << 8) | b[j + 2 + 2 * k] : b[j + 1 + k];
                if (tq === 0) luma = sum;
                j += 1 + n;
            }
        } else if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) {   // SOFn
            const nc = b[i + 9];
            if (nc >= 3) { const hv = b[i + 11]; sub444 = hv === 0x11; }
            else sub444 = false;                                              // greyscale: no chroma at all
        }
        i += 2 + len;
    }
    if (luma == null || sub444 == null) return null;
    const sc = 100 * luma / STD_LUMA_SUM;
    const q = sc <= 100 ? (200 - sc) / 2 : 5000 / sc;
    return { quality: Math.max(1, Math.min(100, Math.round(q))), sub444 };
}

/** HEIF encoder from the `ftyp` box's compatible brands (P2 J13-3, recheck 2026-10-08): the iPhone 13 photo
 *  (`mif1 MiHE MiPr miaf MiHB heic`) and the Galaxy S24 one (`mif1 heic tmap`) vs a libheif file. */
export function heifInfo(b: Uint8Array): HeifInfo | null {
    if (!b || b.length < 16) return null;
    const str = (i: number) => String.fromCharCode(b[i], b[i + 1], b[i + 2], b[i + 3]);
    if (str(4) !== 'ftyp') return null;
    const size = Math.min(b.length, (b[0] << 24 | b[1] << 16 | b[2] << 8 | b[3]) >>> 0);
    const brands: string[] = [];
    for (let i = 16; i + 4 <= size; i += 4) brands.push(str(i));
    const apple = brands.some(x => /^Mi[A-Z]/.test(x));
    return { camera: apple || brands.indexOf('miaf') < 0 };
}

/** PNG colour type 3 (palette) from the IHDR. */
export function pngIndexed(b: Uint8Array): boolean | null {
    if (!b || b.length < 26 || b[0] !== 0x89 || b[1] !== 0x50 || b[2] !== 0x4e || b[3] !== 0x47) return null;
    return b[25] === 3;
}
/** Where a TIFF's first IFD starts (null: not a TIFF head). Writers such as ImageMagick put it after the pixels. */
export function tiffIfdOffset(b: Uint8Array): number | null {
    if (!b || b.length < 8) return null;
    const le = b[0] === 0x49 && b[1] === 0x49, be = b[0] === 0x4d && b[1] === 0x4d;
    if (!le && !be || (le ? b[2] | (b[3] << 8) : (b[2] << 8) | b[3]) !== 42) return null;
    return le ? (b[4] | (b[5] << 8) | (b[6] << 16) | (b[7] << 24)) >>> 0 : ((b[4] << 24) | (b[5] << 16) | (b[6] << 8) | b[7]) >>> 0;
}
/** First IFD of a TIFF: compression, samples per pixel, bits per sample, first strip offset. `ifdChunk` = the
 *  file's bytes from offset `chunkAt` when the IFD lies past the head. Null when it is not a TIFF or the IFD is
 *  in neither. */
export function tiffInfo(head: Uint8Array, ifdChunk?: Uint8Array, chunkAt = 0): TiffInfo | null {
    const ifd = tiffIfdOffset(head); if (ifd == null) return null;
    const le = head[0] === 0x49;
    const byte = (i: number) => (ifdChunk && i >= chunkAt && i - chunkAt < ifdChunk.length ? ifdChunk[i - chunkAt] : i < head.length ? head[i] : 0);
    const has = (i: number, n: number) => (i + n <= head.length) || (!!ifdChunk && i >= chunkAt && i + n - chunkAt <= ifdChunk.length);
    const u16 = (i: number) => (le ? byte(i) | (byte(i + 1) << 8) : (byte(i) << 8) | byte(i + 1));
    const u32 = (i: number) => (le ? (byte(i) | (byte(i + 1) << 8) | (byte(i + 2) << 16) | (byte(i + 3) << 24)) >>> 0 : ((byte(i) << 24) | (byte(i + 1) << 16) | (byte(i + 2) << 8) | byte(i + 3)) >>> 0);
    if (!has(ifd, 2)) return null;
    const n = u16(ifd); if (!has(ifd + 2, n * 12)) return null;
    const out: TiffInfo = { compression: 1, samples: 1, bits: 8, firstStrip: 0, le, planar: false };
    for (let k = 0; k < n; k++) {
        const e = ifd + 2 + k * 12, tag = u16(e), type = u16(e + 2), count = u32(e + 4);
        const inline = type === 3 ? count <= 2 : count <= 1;   // SHORTs fit two to the value field; more = an offset
        const val = inline ? (type === 3 ? u16(e + 8) : u32(e + 8)) : u32(e + 8);
        if (tag === 259) out.compression = val;
        else if (tag === 277) out.samples = val;
        else if (tag === 284) out.planar = val === 2;
        else if (tag === 258) out.bits = inline ? val : (has(val, 2) ? u16(val) : 8);
        else if (tag === 273) out.firstStrip = inline ? val : (has(val, 4) ? (type === 3 ? u16(val) : u32(val)) : 0);
    }
    if ([1, 2, 4, 8, 16, 32].indexOf(out.bits) < 0) out.bits = 8;
    return out;
}
/** B/px the Deflate + Predictor-2 TIFF writer is expected to reach on raw 8-bit-per-channel samples: the
 *  entropy of the horizontal differences (what the predictor leaves for deflate), floored at a flat frame's rate.
 *  16-bit samples are read by their high byte (`hi` = 1 in a little-endian file: the low byte is noise the 8-bit
 *  output drops); `planar` = one plane per channel, so a sample's neighbour is the next sample, not the next pixel. */
export function probeRawDensity(raw: Uint8Array, channels: number, bytesPerSample = 1, hi = 0, planar = false): number {
    const step = Math.max(1, (planar ? 1 : channels) * bytesPerSample); const hist = new Uint32Array(256); let n = 0;
    for (let i = step + Math.min(hi, bytesPerSample - 1); i < raw.length; i += bytesPerSample) { hist[(raw[i] - raw[i - step]) & 255]++; n++; }
    if (!n) return RGB_PNG_FLOOR;
    let h = 0; for (let v = 0; v < 256; v++) if (hist[v]) { const p = hist[v] / n; h -= p * Math.log2(p); }
    return Math.max(RGB_PNG_FLOOR, Math.min(channels, h / 8 * channels));
}

const infoCache: WeakMap<object, JpegInfo | null> = new WeakMap();
const tiffCache: WeakMap<object, TiffInfo | null> = new WeakMap();
const pngCache: WeakMap<object, boolean | null> = new WeakMap();
const heifCache: WeakMap<object, HeifInfo | null> = new WeakMap();
async function headBytes(f: Blob, n: number): Promise<Uint8Array> {
    const part = f.slice(0, Math.min(n, f.size));
    if (typeof (part as any).arrayBuffer === 'function') return new Uint8Array(await part.arrayBuffer());
    return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(new Uint8Array(r.result as ArrayBuffer)); r.onerror = () => rej(r.error); r.readAsArrayBuffer(part); });
}
/** Reads a source's JPEG header once (first 256 KB: EXIF + thumbnail sit before the tables). Never throws. */
export async function primeEstimateSource(f: Blob | null | undefined): Promise<JpegInfo | null> {
    if (!f) return null;
    if (infoCache.has(f)) return infoCache.get(f) ?? null;
    infoCache.set(f, null);
    try {
        const head = await headBytes(f, 256 * 1024);
        heifCache.set(f, heifInfo(head)); pngCache.set(f, pngIndexed(head));
        const at = tiffIfdOffset(head);
        const t = at == null ? null : at + 2 <= head.length ? tiffInfo(head) : tiffInfo(head, await headBytes(f.slice(at, Math.min(f.size, at + 64 * 1024)), 64 * 1024), at);
        if (t && t.compression === 1 && f.size > 64 * 1024) {
            // uncompressed: its bytes say nothing about the content, so probe 256 KB from the middle of the pixels
            const from = Math.max(t.firstStrip, Math.floor((Math.max(t.firstStrip, 8) + f.size) / 2) - 128 * 1024);
            const part = f.slice(from, Math.min(f.size, from + 256 * 1024));
            const raw = typeof (part as any).arrayBuffer === 'function' ? new Uint8Array(await part.arrayBuffer()) : await headBytes(part, part.size);
            t.rawDensity = probeRawDensity(raw, Math.max(1, Math.min(4, t.samples)), t.bits >= 16 ? 2 : 1, t.bits >= 16 && t.le ? 1 : 0, !!t.planar);
        }
        tiffCache.set(f, t);
        const info = jpegInfo(head); infoCache.set(f, info); return info;
    } catch { return null; }
}
function cachedHeif(f: Blob | undefined): HeifInfo | null {
    if (!f) return null;
    if (!infoCache.has(f)) { void primeEstimateSource(f); return null; }
    return heifCache.get(f) ?? null;
}
function cachedTiff(f: Blob | undefined): TiffInfo | null {
    if (!f) return null;
    if (!infoCache.has(f)) { void primeEstimateSource(f); return null; }
    return tiffCache.get(f) ?? null;
}
function cachedIndexed(f: Blob | undefined): boolean | null {
    if (!f) return null;
    if (!infoCache.has(f)) { void primeEstimateSource(f); return null; }
    return pngCache.get(f) ?? null;
}
/** The header info already read for a source; starts the read when it was not asked for yet. */
function cachedInfo(f: Blob | undefined): JpegInfo | null {
    if (!f) return null;
    if (!infoCache.has(f)) { void primeEstimateSource(f); return null; }
    return infoCache.get(f) ?? null;
}

const LOSSY: Record<string, number> = { jpg: 1, jpeg: 1, webp: 0.8, avif: 0.55, heic: 0.55, heif: 0.55 };   // source bytes vs JPEG at equal quality
/** Fixed bytes per file (headers, Huffman tables, the ICO directory); they dominate thumbnails. */
const OVERHEAD: Record<string, number> = { jpg: 620, jpeg: 620, webp: 250, png: 60, ico: 82, gif: 120 };   // gif: anim.gif 200x150 came out 339 B (final2 browser8)
const LOSSLESS = ['png', 'gif', 'ico', 'apng'];
const RAW = ['bmp', 'ppm', 'pgm', 'pbm', 'tiff', 'tif', 'psd'];
/** PackBits (RLE) bytes per raw byte vs the Chrome-JPEG-q85 density `e` of the content: ag-psd writes the
 *  layer AND the composite RLE-coded (encodePsdBlob), so a noisy photo costs 2x raw and a flat frame ~4 %.
 *  Measured 2026-10-06 with ag-psd on IMG_2041 (e 0.0075: 0.02), beach (0.013: 0.017), m3_land (0.03: 0.18),
 *  family_photo (0.062: 0.71), photo.jpg (0.35: 0.96); final2 browser8 photo-600x800.psd 2 887 326 B (2.0x raw). */
const RLE: [number, number][] = [[0.012, 0.02], [0.03, 0.18], [0.062, 0.71], [0.15, 0.95], [0.35, 1]];
function rleRatio(e: number): number {
    if (e <= RLE[0][0]) return RLE[0][1];
    for (let i = 1; i < RLE.length; i++) {
        const [e0, r0] = RLE[i - 1], [e1, r1] = RLE[i];
        if (e <= e1) return r0 + (r1 - r0) * Math.log(e / e0) / Math.log(e1 / e0);
    }
    return 1;
}
/** Typical photo when the source says nothing (no bytes, or an uncompressed container). */
const DEFAULT_E = 0.1;
const JPEG_FLOOR = 0.004;   // B/px Chrome's q85 4:2:0 JPEG reaches on a flat 12 MP frame (quad_land: 0.0063)
const PNG_FLOOR = 0.022;
const RGB_PNG_FLOOR = 0.004;   // B/px the editor's own RGB PNG writer reaches on a flat frame (quad_land 0.0035)    // B/px Chrome's PNG encoder writes for a flat frame, however small the source PNG was
/** When the header was not read (yet): neutral, as if the source were Chrome's own q85 4:2:0. */
const ASSUMED_JPEG: JpegInfo = { quality: 85, sub444: false };

/** JPEG bytes vs quality, relative to q85 (libjpeg standard tables). */
const Q_CURVE: [number, number][] = [[1, 0.15], [10, 0.25], [30, 0.4], [50, 0.55], [70, 0.75], [85, 1], [90, 1.25], [95, 1.75], [100, 3.2]];
export function qualityFactor(q: number): number {
    const x = Math.max(1, Math.min(100, q || 85));
    for (let i = 1; i < Q_CURVE.length; i++) {
        const [q0, f0] = Q_CURVE[i - 1], [q1, f1] = Q_CURVE[i];
        if (x <= q1) return f0 + (f1 - f0) * (x - q0) / (q1 - q0);
    }
    return Q_CURVE[Q_CURVE.length - 1][1];
}

const norm = (f: string) => (f || '').toLowerCase().replace(/^\./, '');

/** Chrome PNG density (RGBA, `canvas.toBlob`: transparent outputs) from a Chrome-JPEG-q85 density: flat
 *  frames sit on the PNG floor, photos ~14x. */
function pngFromE(e: number): number {
    return Math.max(PNG_FLOOR, Math.min(4, 14 * e * Math.min(1, Math.pow(e / 0.06, 3))));
}
/** The editor's own opaque PNG (RGB, pako level 6, encodePng): flat frames compress to ~0.004 B/px (beach,
 *  cmyk, quad_land 0.0035-0.0075), photos saturate near 2.5 B/px (family_photo e 0.062: 0.63, holiday 0.16:
 *  1.42, photo.jpg 0.35: 2.0). Measured 2026-10-06 on the 23 QA fixtures (pako + filterRows, as shipped). */
function rgbPngFromE(e: number): number {
    return Math.max(RGB_PNG_FLOOR, 0.6 * e, 2.5 * (1 - Math.exp(-5 * e)) * Math.min(1, Math.pow(e / 0.06, 3)));
}
/** JPEG-q85 density from a lossless (PNG) source density: grid1200x800 0.0042 -> 0.011, alpha_1200x800
 *  0.0093 -> 0.018, a 1600x1200 photo PNG at 1.3 B/px -> 0.117 (Chrome). Synthetic noise (photo.png, 2.0 ->
 *  0.32) sits far above the curve: PNG saturates there while JPEG keeps growing; real photos do not. */
const eFromPng = (p: number) => Math.max(0.011, 0.106 * Math.pow(p, 0.385));
/** JPEG-q85 density from a WebP source: l05_land 0.0027 -> 0.019, photo.webp 0.38 -> 0.32. */
const eFromWebp = (w: number) => Math.max(0.012, 0.55 * Math.pow(w, 0.57));
/** JPEG-q85 density from a HEIC / AVIF source: 1.26x on lean files (real.heic 1848x4000 0.052 B/px -> 0.065,
 *  final2 heicname), 0.6x on dense ones (usage QA J10: iphone 4032x3024 0.229 B/px -> 0.131 B/px as the 3024
 *  square crop; J15 IMG_2041 0.46 -> 149 KB at 1200x900); log-linear between 0.05 and 0.2 B/px. */
function eFromHeic(h: number): number {
    const t = Math.max(0, Math.min(1, Math.log(Math.max(h, 1e-6) / 0.05) / Math.log(4)));
    return Math.max(0.012, h * 1.26 * Math.pow(0.6 / 1.26, t));
}
/** WebP vs JPEG at equal quality (Chrome): ~0.3 on flat frames, ~0.55 on photos, ~0.9 on noisy detail
 *  (holiday e 0.16: 0.66, photo.jpg 0.35: 0.89). */
function webpRatio(e: number): number {
    const t = Math.max(0, Math.min(1, (Math.log(e) - Math.log(0.015)) / (Math.log(0.35) - Math.log(0.015))));
    return 0.3 + 0.7 * t;   // J14-2: photo.webp 1920x1080 at 960x540 wrote 107 KB (0.6 t read 78 KB)
}
/** AVIF the browser writes (jsquash) vs JPEG at equal quality: denser than our server's, not leaner — J14-2:
 *  a q60 4032x3024 AVIF (0.055 B/px) re-encoded at q85 wrote 1.09 MB for the 16:9 crop, 543 KB at 50 %. */
const AVIF_BROWSER = 0.57;   // verify round 2026-10-08: the fixed encoder wrote 374,767 B for that crop (1.63 read 1.1 MB, 2.9x)
/** HEIC/AVIF our servers write, per source byte (J13-3: rotating real iPhone HEICs gave 2.7x and 3.2x). */
const HEIF_SERVER_PER_SRC = 2.9;
/** JPEG-q85 density per HEIC byte for a phone's HEIC (see heifInfo). */
const HEIF_CAMERA_TO_JPEG = 1.33;

function rawBytes(fmt: string, px: number, hasAlpha: boolean): number | null {
    const ch = hasAlpha ? 4 : 3;
    switch (fmt) {
        case 'bmp': return px * ch + 54;
        case 'ppm': return px * 3 + 20;
        case 'pgm': return px + 20;
        case 'pbm': return Math.ceil(px / 8) + 20;
        default: return null;
    }
}
const isTiff = (f: string) => f === 'tif' || f === 'tiff';

/**
 * TIFF output (usage QA 2026-10-07: J2-9, J8-5, J3-5). Both writers compress now (the browser deflate/LZW,
 * PLAN B; the server LZW), so a TIFF is no longer "exact". The server keeps the source's bit depth (a 16-bit
 * scan stays 16-bit); the browser writes 8 bits (1-bit stays 1-bit). A compressed TIFF source tells its own
 * content density: the server's crops came out 1.2-1.5x it (scan 5.5 MB, photo 8.8 MB, grey 1.7 MB, scan16
 * 19.4 MB), a bilevel page has a floor (89 KB). An uncompressed source says nothing about its content: about
 * 0.9 of raw at 16 bits (LZW gains little on noisy low bits; J3-5 43 MB), 0.7 at 8. Other sources: the
 * content density `e` as the editor's RGB deflate writer.
 */
function tiffBytes(o: EstimateInput, outPx: number, e: number | null, region = 1): number {
    const src = o.source; const server = o.route === 'server';
    const srcBits = src?.bitsPerSample && src.bitsPerSample > 0 ? src.bitsPerSample : 8;
    const outBits = server ? srcBits : Math.min(8, srcBits);
    const ch = o.hasAlpha ? 4 : 3;
    const bytesPerPx = (bits: number) => (bits === 1 ? 1 / 8 : ch * bits / 8);
    const srcPx = src && src.width > 0 && src.height > 0 ? src.width * src.height : 0;
    const ti = src && isTiff(norm(src.format)) ? (src.tiff !== undefined ? src.tiff : cachedTiff(src.file)) : null;
    if (!server && ti && srcPx > 0 && src!.bytes > 0 && outBits === 8) {
        // review9 IMAGEEDITOR-04: the browser writes Deflate + Predictor 2, RGB(A) 8-bit, whatever the source was
        const basePx = srcPx * region, r = outPx / basePx;
        if (ti.compression === 1) {
            // uncompressed: the content probe (a flat 6000x4000 frame wrote 24,567 B at 3000 px, not 0.7 x raw)
            if (ti.rawDensity != null) return Math.round(Math.max(RGB_PNG_FLOOR, ti.rawDensity) * outPx * (ch / Math.max(3, Math.min(4, ti.samples))) + 1024);
        } else if (ti.compression === 7 || ti.compression === 6) {
            // JPEG-in-TIFF: lossy bytes that expand (scan_01 6000x4000 at 2000 px wrote 4,989,056 B; jpeg_1264 at 200 %
            // 6,185,839 B): its density as a JPEG's, then the editor's lossless RGB writer, ~0.8 of it with the predictor
            const ej = eFromJpeg(src!.bytes / srcPx * (3 / Math.max(3, Math.min(4, ti.samples))), ASSUMED_JPEG);
            const dens = 0.8 * rgbPngFromE(ej) * (ch / 3);
            return Math.round(dens * basePx * Math.pow(r, r > 1 ? 0.7 : 1) + 1024);
        }
    }
    if (src && isTiff(norm(src.format)) && srcPx > 0 && src.bytes > 0) {
        const s = src.bytes / srcPx;
        if (s < 0.9 * bytesPerPx(srcBits)) return Math.round(Math.max(outBits === 1 ? 0.015 : 0.002, 1.4 * s * (outBits / srcBits)) * outPx + 1024);
        return Math.round(outPx * bytesPerPx(outBits) * (outBits >= 16 ? 0.9 : 0.7) + 1024);
    }
    const dens = rgbPngFromE(e ?? DEFAULT_E) * (ch / 3) * (server ? 1.3 : 1);
    return Math.round(Math.min(dens * outBits / 8, bytesPerPx(outBits)) * outPx + 1024);
}

/**
 * Chrome-JPEG-q85-4:2:0 density `e` (B/px at the source's size) of a JPEG source. Re-encoding scales the
 * bytes by the quality ratio (a q92 source halves, a q71 one grows ~1.4x) and 4:4:4 → 4:2:0 drops ~¼;
 * measured on 13 JPEG fixtures (L1-model-est/chrome-calib.json): IMG_2041 q92 4:4:4 0.54x, holiday q85 1.0x,
 * family_photo q71 1.45x. Very dense sources are capped (a q85 JPEG of a photo stays under ~0.27 j^0.42).
 */
function eFromJpeg(j: number, info: JpegInfo): number {
    const ratio = qualityFactor(85) / qualityFactor(info.quality);
    const k = (ratio > 1 ? Math.pow(ratio, 1.4) : ratio) * (info.sub444 ? 0.75 : 1);
    return Math.max(JPEG_FLOOR, k * j);
}

/** Bytes ∝ (pixel ratio)^x. Shrinking packs more detail into each pixel, more so on flat frames (their
 *  blocks fill with edges) than on noisy detail, which is already dense: measured on the QA fixtures at
 *  r = 1, ¼, 1/16, 1/100 (JPEG x 0.66-0.74 flat, 0.85-0.91 noisy; WebP 0.45-0.65 flat, ~0.9 noisy; PNG ~0.7
 *  flat, 0.85 photo). Upscaled pixels are interpolated and compress well (x ~0.7 at 1.5x). */
function shrinkExponent(r: number, e: number, fmt: string, bpp: number, srcLossless = true): number {
    const lossless = LOSSLESS.indexOf(fmt) >= 0;
    if (r > 1) return lossless ? 0.8 : 0.7;   // J10-5: a flat PNG upscaled 2x wrote 33 KB (0.7 read 25 KB)
    // Usage QA 2026-10-07 (J9-8, J10-5, J11-10, J15-8): shrinking AVERAGES pixels, so on dense, noisy content the
    // density falls with the size (x > 1): a 1.4 B/px PNG logo at 1/16 of its pixels came out 0.7 B/px, q88-q90
    // photos at 1080-1200 px 0.13 B/px. A lossless output of a lossy source stops at 1 (Chrome's PNG of a JPEG photo
    // stays near saturation: 800x600 -> 256x192 ICO 2.3 B/px).
    // verify round (J4-3): a crisp opaque screenshot (0.6 B/px) shrunk to 0.4 of its pixels wrote 344 KB = x 0.69 - the
    // resample blurs text into new colours; the ramp to the noisy-photo x 1.2 starts at 0.9 B/px, not 0.3
    if (lossless) return bpp < 0.9 ? 0.7 : Math.min(srcLossless ? 1.2 : 1, 0.7 + 0.5 * Math.max(0, Math.min(1, (bpp - 0.9) / 0.5)));
    const t = Math.max(0, Math.min(1, Math.log(e / 0.015) / Math.log(0.35 / 0.015)));
    if (fmt === 'webp' || fmt === 'avif' || fmt === 'heic' || fmt === 'heif') return 0.6 + 0.3 * t;
    // JPEG: 0.7 on flat frames, ~0.91 at e 0.09 (exif6 at 1080: 167 KB), ~1.1 at e 0.18 (J15 photo.jpg, J11)
    return Math.max(0.7, Math.min(1.35, 0.91 + 2.3 * (e - 0.089)));
}

/** Area of the bounds of a turned w:h frame over the frame's own area. */
function turnedArea(deg: number, aspect: number): number {
    const a = Math.abs(deg % 180) * Math.PI / 180, c = Math.abs(Math.cos(a)), s = Math.abs(Math.sin(a));
    const w = Math.max(1e-6, aspect), h = 1;
    return ((w * c + h * s) * (w * s + h * c)) / (w * h);
}

export function estimateOutputBytes(input: EstimateInput): number {
    const o: EstimateInput = input.shapeAlpha && !input.hasAlpha ? { ...input, hasAlpha: true } : input;   // a Circle writes alpha
    const region = o.region && o.region > 0 && o.region < 1 ? o.region : 1;
    const fmt = norm(o.format);
    const outPx = Math.max(1, Math.round(o.size.width) * Math.round(o.size.height));
    const raw = rawBytes(fmt, outPx, o.hasAlpha);
    if (raw != null) return raw;
    const server = o.route === 'server';

    const src = o.source;
    const srcFmt = src ? norm(src.format) : '';
    const srcPx = src && src.width > 0 && src.height > 0 ? src.width * src.height : 0;
    const known = !!src && src.bytes > 0 && srcPx > 0 && RAW.indexOf(srcFmt) < 0 && src.bytes / srcPx < 2.5;
    const s = known ? src!.bytes / srcPx : 0;
    // review9 IMAGEEDITOR-05: the density is the crop region's share of the source, and a source denser than 2.5 B/px
    // (a 1 x 1 PNG of 311 B: its header) still has only its own pixels' detail - an upscale of it is interpolated
    const sized = !!src && srcPx > 0;
    const basePx = known ? srcPx * region : sized && outPx > srcPx * region ? srcPx * region : outPx;
    const srcLossless = LOSSLESS.indexOf(srcFmt) >= 0;
    const outLossless = LOSSLESS.indexOf(fmt) >= 0;
    const turned = !!o.fineRotation && Math.abs(o.fineRotation.angle % 90) > 1e-6;

    // JPEG-q85 density of the source, in Chrome's terms
    let e: number;
    if (!known) e = DEFAULT_E;
    else if (srcLossless) e = eFromPng(s);
    else if (srcFmt === 'jpg' || srcFmt === 'jpeg') e = eFromJpeg(s, src!.jpeg ?? cachedInfo(src!.file) ?? ASSUMED_JPEG);
    else if (srcFmt === 'webp') e = eFromWebp(s);
    else {   // HEIC / HEIF / AVIF sources
        const h = src!.heif !== undefined ? src!.heif : cachedHeif(src!.file);
        // a phone's HEIC: Chrome's q85 JPEG of the iPhone 13 photo (0.116 B/px) wrote 0.160 B/px (1.375x),
        // the Galaxy S24 one (0.052) 0.065 (1.26x); libheif files keep the density curve (P2 J13-3)
        e = h && h.camera ? Math.max(0.012, HEIF_CAMERA_TO_JPEG * s) : eFromHeic(s);
    }

    if (isTiff(fmt)) return tiffBytes(o, outPx, known ? e : null, region);

    // review9 save-as-3e: an indexed PNG keeps its palette through rotate / flip / crop (img-encode writes PLTE when
    // <= 256 colours); after a resample it is written RGBA - icon_indexed.png 512 -> 256 wrote 35,946 B, 2.8x the source
    const indexed = fmt === 'png' && known && srcFmt === 'png' ? (src!.indexed !== undefined ? src!.indexed : cachedIndexed(src!.file)) : null;
    // without the hints a smaller output may be a crop (palette kept) or a downscale (RGBA): no guess, the rule below
    // (review10: a crop-only 512 -> 256 read 30 KB as a resample; real ~3.3 KB)
    const near = Math.abs(outPx - srcPx * region) <= 0.01 * srcPx * region;
    const resampled = o.resampled ?? (turned || outPx > srcPx * region * 1.01 ? true : near ? false : undefined);
    if (indexed && resampled !== undefined) {
        if (!resampled) return Math.max(64, Math.round(s * outPx + (OVERHEAD.png ?? 0)));
        // RGBA of the same pixels is ~3.6-4.1x the palette file (rotate: icon 46,713 B, logo_pq 15,560 B); a resample
        // adds new edge colours, so the bytes fall slowly with the pixels (x 0.35)
        const rgba = 3.8 * s * basePx, r0 = outPx / basePx;
        return Math.max(64, Math.round(rgba * Math.pow(r0, r0 > 1 ? 0.8 : 0.35) + (OVERHEAD.png ?? 0)));
    }

    // PSD: layer + composite, each PackBits-coded (rleRatio); ag-psd's thumbnail and resources ~4 KB
    if (fmt === 'psd') return Math.round(2 * outPx * (o.hasAlpha ? 3.5 : 3) * rleRatio(e) + 4096);

    let bpp: number;
    if (outLossless) {
        // opaque PNG: the editor's own RGB writer; transparent (or turned: the corners may be): Chrome's RGBA
        const rgb = fmt === 'png' && !o.hasAlpha && !turned;
        if (rgb) bpp = known && srcLossless ? Math.max(s, RGB_PNG_FLOOR) : rgbPngFromE(e);
        else bpp = known && srcLossless ? Math.max(s, PNG_FLOOR * (o.hasAlpha ? 1.6 : 1)) : pngFromE(e) * (o.hasAlpha && known ? 1.15 : 1);
        if (turned) bpp = Math.max(bpp * 1.15, 0.055);   // anti-aliased edges + corners (grid1200x800 10°: 0.053)
        if (fmt === 'gif') bpp = Math.min(0.6, bpp * 0.5);
    } else {
        const q = qualityFactor(o.quality);
        const heif = fmt === 'avif' || fmt === 'heic' || fmt === 'heif';
        const srcHeif = srcFmt === 'avif' || srcFmt === 'heic' || srcFmt === 'heif';
        if (heif && server) bpp = (known && srcHeif ? HEIF_SERVER_PER_SRC * s : 2 * e) * q;
        else if (fmt === 'avif') bpp = e * q * AVIF_BROWSER;
        else bpp = e * q * (fmt === 'webp' ? webpRatio(e) : heif ? 0.7 * webpRatio(e) : (LOSSY[fmt] ?? 1));
    }

    // fewer pixels carry more detail each; upscaled pixels are interpolated and compress well. Corners a
    // turn adds hold no photo: they count at the flat-frame rate (PNG pays them through `bpp` above).
    // an ICO is a square: a non-square photo is padded with transparent rows that cost next to nothing (J9-8)
    const icoPad = fmt === 'ico' && known ? Math.min(src!.width / src!.height, src!.height / src!.width) : 1;
    const photoPx = turned && o.fineRotation!.corners ? outPx / turnedArea(o.fineRotation!.angle, known ? src!.width / src!.height : o.size.width / o.size.height) : outPx * icoPad;
    const r = photoPx / basePx;
    let bytes = bpp * basePx * Math.pow(r, shrinkExponent(r, e, fmt, bpp, !known || srcLossless));
    // an upscale past 4x is interpolation: it grows slowly (a 1 x 1 PNG at 600 x 600 wrote 2,338 B, the 0.8 rule read 354 KB)
    // (only a source we know nothing of / denser than 2.5 B/px: a known photo has no measured upscale fixture past 4x)
    if (r > 4 && !known) bytes = Math.max(bpp * basePx * Math.pow(4, shrinkExponent(4, e, fmt, bpp, !known || srcLossless)) * Math.pow(r / 4, 0.3), (outLossless ? (o.hasAlpha ? PNG_FLOOR * 1.6 : RGB_PNG_FLOOR) : JPEG_FLOOR) * outPx);
    if (photoPx < outPx) bytes += (fmt === 'ico' && !turned ? PNG_FLOOR : outLossless ? bpp : JPEG_FLOOR) * (outPx - photoPx);
    // verify round (J4-3): a flat transparent logo resampled smaller gains anti-aliased edges in every row: a 0.027 B/px
    // 1024 PNG logo at 800 px wrote 49,916 B (0.078 B/px of output) - the shrink rule read 26 KB
    if (outLossless && fmt !== 'gif' && o.hasAlpha && known && srcLossless && r < 0.95 && s < 0.1) bytes = Math.max(bytes, 0.078 * outPx);
    if (turned && !outLossless && e < 0.03) bytes *= 1.3;   // a turned flat frame's new edges cost 1.2-1.8x; photos ~1x
    return Math.max(64, Math.round(bytes + (OVERHEAD[fmt] ?? 0)));
}

/** Below this relative difference the estimate cannot honestly tell growth from shrinkage (it is good to
 *  ±30 %): a near-unchanged re-encode read "−5 %" while the file came out +1 % (final2 NEW-4). */
export const SAME_SIZE_BAND = 0.1;
/** Past this the percentage stops meaning anything ("+4304689%", J16-8): the line says "N× larger". */
export const TIMES_ABOVE_PCT = 999;
/** The Output line's change vs the original: `same` = say "≈ same size", never a signed percentage.
 *  `text` is what the line shows: "≈ same size", "+43%", "−20%" or, above +999 %, "44445× larger" (J16-8).
 *  A shrink never reads −100 % while a file remains (J9-8: a 4 KB icon of a 1.4 MB logo): it caps at −99 %. */
export interface SizeDelta { pct: number; up: boolean; same: boolean; text: string; times?: number; }
export function sizeDelta(estimate: number, original: number): SizeDelta | null {
    if (!(original > 0) || !(estimate >= 0)) return null;
    const rel = estimate / original - 1;
    let pct = Math.abs(Math.round(100 * rel));
    if (rel < 0 && pct >= 100 && estimate > 0) pct = 99;
    const up = rel > 0, same = Math.abs(rel) < SAME_SIZE_BAND;
    if (same) return { pct, up, same, text: '≈ same size' };
    if (up && pct > TIMES_ABOVE_PCT) { const times = Math.round(estimate / original); return { pct, up, same, text: `${times}× larger`, times }; }
    return { pct, up, same, text: `${up ? '+' : '−'}${pct}%` };
}
